/**
 * WorkspacePage - Main page component for Product Manager IDE
 * Orchestrates workspace header, mode content, and detail sidebar
 */

import { useEffect, useState } from 'react'
import { WorkspaceHeader } from './WorkspaceHeader'
import { WorkspaceModeRenderer } from './WorkspaceModeRenderer'
import { DetailSidebar } from './DetailSidebar'
import { GoalSelectionWizard } from './GoalSelectionWizard'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useWorkspaceStore, type WorkspaceMode } from '@/stores/workspaceStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { LayoutGrid } from 'lucide-react'

export function WorkspacePage() {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { setActiveMode } = useWorkspaceStore()
  const { fetchJourney, hasGoal, isLoading: isJourneyLoading } = useJourneyStore()
  const [showGoalWizard, setShowGoalWizard] = useState(false)
  const [hasCheckedGoal, setHasCheckedGoal] = useState(false)

  // Keyboard shortcuts for mode switching (Cmd/Ctrl + 1-4)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return
      }

      // Cmd/Ctrl + 1-4 for mode switching
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        const modeMap: Record<string, WorkspaceMode> = {
          '1': 'full-ai',
          '2': 'canvas',
          '3': 'smart-doc',
          '4': 'entities',
        }

        const mode = modeMap[e.key]
        if (mode) {
          e.preventDefault()
          setActiveMode(mode)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [setActiveMode])

  // Fetch journey data and prompt for goal selection if needed
  useEffect(() => {
    if (!currentAccount?.id || !currentProject?.id) return

    fetchJourney(currentAccount.id, currentProject.id).then(() => {
      setHasCheckedGoal(true)
    })
  }, [currentAccount?.id, currentProject?.id, fetchJourney])

  // Show goal wizard if no goal selected (after loading completes)
  useEffect(() => {
    if (hasCheckedGoal && !isJourneyLoading && !hasGoal()) {
      setShowGoalWizard(true)
    }
  }, [hasCheckedGoal, isJourneyLoading, hasGoal])

  // Guard: Require account and project
  if (!currentAccount || !currentProject) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <LayoutGrid
            size={48}
            className="mx-auto mb-3"
            style={{ color: 'var(--color-warm-300)' }}
          />
          <p className="text-text-muted">
            Please select a project to access the Workspace
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <WorkspaceHeader />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Mode Content (takes remaining space) */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ backgroundColor: 'var(--color-warm-50)' }}
        >
          <WorkspaceModeRenderer onOpenGoalWizard={() => setShowGoalWizard(true)} />
        </div>

        {/* Detail Sidebar (fixed width) */}
        <DetailSidebar onOpenGoalWizard={() => setShowGoalWizard(true)} />
      </div>

      {/* Goal Selection Wizard */}
      <GoalSelectionWizard
        isOpen={showGoalWizard}
        onClose={() => setShowGoalWizard(false)}
        onGoalSelected={() => setShowGoalWizard(false)}
      />
    </div>
  )
}
